<template>
  <div class="btn-audio-container">
    <div
      class="btn-audio"
      :class="{
        disabled: disabled || !isReady,
        invalid: !!errorMessage,
      }"
      @mouseover="mouseover"
      @mouseleave="mouseleave"
    >
      <button type="button" class="btn-audio-toggle" @click.prevent="playPause">
        <SvgIcon
          :name="`components/${isPlaying ? 'pause' : 'play'}`"
          :title="isPlaying ? 'Pause' : 'Play'"
          :width="isPlaying ? '12' : '10'"
          :height="isPlaying ? '12' : '16'"
          :style="isPlaying ? undefined : 'margin-left: 1px;'"
        />
      </button>

      <div class="btn-audio-wave-container">
        <div ref="wave" />
      </div>
    </div>

    <div v-if="errorMessage" class="btn-audio-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import SvgIcon from 'vue-svgicon/dist/components/svgicon.common'

import '../../icons/components/play'
import '../../icons/components/pause'

const waveColor = '#e2e2e2'
const waveColorDisabled = '#b2b2b2'
const progressColor = '#242424'
const progressColorDisabled = '#b2b2b2'
const progressColorHover = '#00778f'

export default {
  components: {
    SvgIcon,
  },
  props: {
    /**
     * URL of the audio source.
     */
    src: {
      type: String,
      required: true,
    },

    /**
     * Text to show when the form group has an invalid state
     */
    errorMessage: {
      type: String,
      default: undefined,
    },

    /**
     * Button does not react on mouse events.
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Height of the waveform bars. Higher number than 1 will increase the waveform bar heights.
     */
    barHeight: {
      type: Number,
      default: 1,
    },
  },
  data: () => ({
    wavesurfer: null,
    isPlaying: false,
    isReady: false,
  }),
  watch: {
    disabled(val, oldVal) {
      let finalWaveColor = waveColorDisabled
      let finalProgressColor = progressColorDisabled

      if (!val && oldVal) {
        finalWaveColor = waveColor
        finalProgressColor = progressColor
      } else {
        this.pause()
      }

      this.wavesurfer.setWaveColor(finalWaveColor)
      this.wavesurfer.setProgressColor(finalProgressColor)
    },
  },
  mounted() {
    this.initWaveSurfer()
  },

  methods: {
    async initWaveSurfer() {
      try {
        const WaveSurfer = await import('wavesurfer.js')
        this.wavesurfer = WaveSurfer.create({
          container: this.$refs['wave'],
          barWidth: 2,
          barHeight: this.barHeight,
          barGap: 2,
          waveColor: this.disabled ? waveColorDisabled : waveColor,
          progressColor,
          cursorWidth: 0,
          height: 38 + 20,
        })

        this.wavesurfer.on('error', (e) => {
          console.error(e)
        })

        this.wavesurfer.on('ready', () => {
          this.isReady = true
        })

        this.wavesurfer.load(this.src)
      } catch (error) {
        console.error(error)
      }
    },

    playPause() {
      if (this.isReady) {
        if (this.isPlaying) {
          this.pause()
        } else {
          this.play()
        }
      }
    },
    pause() {
      this.wavesurfer.pause()
      this.isPlaying = false
    },
    play() {
      this.wavesurfer.play()
      this.isPlaying = true
    },
    mouseover() {
      if (!this.disabled) {
        this.wavesurfer.setProgressColor(progressColorHover)
      }
    },
    mouseleave() {
      if (!this.disabled) {
        this.wavesurfer.setProgressColor(progressColor)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-audio {
  height: 4rem;
  border-radius: 2px;
  border: 1px solid $gray-200;
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  padding: 1rem;
  background-color: #fff;

  &:hover:not(.disabled) {
    border-color: $cyan-700;

    .btn-audio-toggle {
      background-color: $cyan-700;
    }
  }

  &.disabled {
    pointer-events: none;
    background-color: $gray-100;
    border-color: $gray-200;

    .btn-audio-toggle {
      background-color: $gray-300;
    }
  }

  &.invalid {
    border-color: $red-500 !important;
  }

  &-error {
    color: $red-500;
    font-size: $font-size-sm;
    margin-top: 0.5rem;
    line-height: 1;
  }

  &-toggle {
    width: 32px;
    height: 32px;
    background-color: $gray-900;
    color: #fff;
    border-radius: 50%;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    cursor: pointer;
    margin-right: 16px;
    border: none;

    &:focus {
      outline: 0;
    }
  }

  &-wave-container {
    position: relative;
    // width: 100%;
    flex-grow: 1;
  }
}
</style>
